import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import PlaySongProgressBar from "./progressbar/PlaySongProgressBar";
import * as Tone from "tone";
import {
    FaPlay,
    FaPause,
    FaStepBackward,
    FaRedoAlt,
    FaDownload,
    FaHeart,
    FaPen,
} from "react-icons/fa";
import {
    FaRegHeart,
} from "react-icons/fa";
import Button from "./buttons/Button";
import CircularProgressBar from "./progressbar/CircularProgressBar";
import { handleWAVDownloadClick } from "../util/PlayerUtil.js";
import {
    loadSynth,
    disposeSynths,
    createBufferedMidiEvents,
    getTuning,
} from "../util/PlayerUtil.js";
import Download from "./popover/content/Download";
import Popover from "./popover/Popover";
import PopoverContentContainer from "./popover/PopoverContentContainer";
import { delay } from "../util/AppUtil.js";
import { showToastInfo } from "../style/ShowToast";
import {
    FONT_SIZE_XS,
    FONT_SIZE_4XS,
    BUTTON_FONT_FAMILY,
    BUTTON_FONT_WEIGHT,
    BUTTON_LETTER_SPACING,
} from "../style/fontSettings";
import {
    MELOGY_WHITE,
    MELOGY_TRANSPARENT,
    MELOGY_BLACK,
} from "../style/colors";
import { getStyleColor, findFarthestColor, getStyleBackgroundImage } from "../util/StyleSettingsUtil";
import { getButtonHoverColor } from "../util/ButtonUtil";
import { alpha } from "@mui/system";

const SongsSectionContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    position: relative;
`;

const SongsHeader = styled.div`
    width: 100%;
    padding: 16px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${MELOGY_TRANSPARENT};
    z-index: 1;
`;

const SongsHeaderContent = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0 80px; /* Padding to account for side overlays */
    box-sizing: border-box;
`;

const SongsTitle = styled.div`
    font-family: ${BUTTON_FONT_FAMILY};
    font-weight: ${BUTTON_FONT_WEIGHT};
    font-size: ${FONT_SIZE_XS};
    color: ${MELOGY_WHITE};
    text-transform: uppercase;
    letter-spacing: ${BUTTON_LETTER_SPACING};
`;

const SongsContent = styled.div`
    width: 100%;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
`;

const SongsBackground = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
`;

const StyledSwiperSlide = styled(SwiperSlide)`
    width: 120px !important;
    height: 100% !important;
    
    @media (min-width: 768px) {
        width: 160px !important;
    }
    
    @media (min-width: 1024px) {
        width: 200px !important;
    }
    
    @media (min-width: 1440px) {
        width: 240px !important;
    }
`;

const SongContainers = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const SongCard = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    box-sizing: border-box;
    background-color: ${({ backgroundColor }) => backgroundColor || "transparent"};
    background-image: ${({ backgroundImage }) => backgroundImage ? `url(${backgroundImage})` : 'none'};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    border-radius: 8px;
    border: ${({ isActive }) => {
        if (!isActive) return "none";
        // Use white with 0.8 opacity for border
        return "2px solid rgba(255, 255, 255, 0.8)";
    }};
    box-shadow: none;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${({ backgroundColor }) => backgroundColor || "transparent"};
        opacity: ${({ backgroundImage }) => backgroundImage ? 0 : 1};
        border-radius: 8px;
        z-index: 0;
        display: ${({ backgroundImage }) => backgroundImage ? 'none' : 'block'};
    }
    
    & > * {
        position: relative;
        z-index: 1;
    }
    
    &:hover {
        opacity: 0.9;
    }
`;

const SongHeader = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px;
    position: relative;
    min-height: 40px;
`;

const HeaderButtonsContainer = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
    display: flex;
    gap: 8px;
    align-items: center;
`;

const DownloadButtonContainer = styled.div`
    z-index: 10;
`;

const FavoriteButtonContainer = styled.div`
    z-index: 10;
`;

const EditButtonContainer = styled.div`
    z-index: 10;
`;

const SongNameContainer = styled.div`
    height: auto; 
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    overflow: hidden; 
    padding: 8px;
    background-color: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border-radius: 4px;
`;

const SongNameWrapper = styled.div`
    height: auto; 
    max-height: 60%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    overflow: hidden; 
    padding: 8px;
    flex: 1;
`;

const SongNameText = styled.div`
    font-size: clamp(${FONT_SIZE_4XS}, 2vw, ${FONT_SIZE_XS}); 
    font-family: ${BUTTON_FONT_FAMILY};
    font-weight: ${BUTTON_FONT_WEIGHT};
    letter-spacing: ${BUTTON_LETTER_SPACING};
    color: ${MELOGY_WHITE};
    text-align: center;
    word-break: break-word; 
    white-space: normal;
    overflow-wrap: break-word; 
    display: block;
    width: 100%; 
    padding: 8px;
    background-color: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-radius: 0;
`;

const StyleNameText = styled.div`
    font-family: ${BUTTON_FONT_FAMILY};
    font-weight: ${BUTTON_FONT_WEIGHT};
    font-size: ${FONT_SIZE_4XS};
    color: ${MELOGY_WHITE};
    text-align: left;
    word-break: break-word;
    overflow-wrap: break-word;
    opacity: 0.7;
    margin-top: 4px;
    width: 100%;
    background-color: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-radius: 0;
    padding: 4px 8px;
`;

const SongPlayerContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-height: 80px;
    justify-content: center;
`;

const ProgressContainer = styled.div`
    height: 16px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
`;

const SongPlayerControls = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    position: relative;
`;

const SimpleControlsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
`;

// Individual song player component
const SongPlayer = ({ 
    song, 
    isActive, 
    isPlaying,
    progress,
    selectedInstruments,
    selectedStyle,
    onPlayPause,
    onProgressChange,
    onRewind,
    onLoop,
    isLooping,
    isInitializing,
}) => {
    const midi = song ? song.midi : null;
    
    if (!midi) {
        return (
            <SongPlayerContainer>
                <SongNameText>No MIDI data</SongNameText>
            </SongPlayerContainer>
        );
    }

    return (
        <SongPlayerContainer onClick={(e) => e.stopPropagation()}>
            {isActive && (
                <>
                    <ProgressContainer>
                        <PlaySongProgressBar
                            progress={progress}
                            handleDrag={onProgressChange}
                            handleClick={onProgressChange}
                        />
                    </ProgressContainer>
                    <SongPlayerControls>
                        <SimpleControlsContainer>
                            <Button
                                height="32px"
                                width="32px"
                                justifyContent="flex-start"
                                buttonColor="white"
                                onClick={onRewind}
                                icon={<FaStepBackward style={{ fontSize: "16px" }} />}
                            />
                            {isInitializing ? (
                                <CircularProgressBar />
                            ) : (
                                <Button
                                    height="32px"
                                    width="32px"
                                    justifyContent="flex-start"
                                    buttonColor="white"
                                    onClick={onPlayPause}
                                    icon={
                                        isPlaying ? (
                                            <FaPause style={{ fontSize: "16px" }} />
                                        ) : (
                                            <FaPlay style={{ fontSize: "16px" }} />
                                        )
                                    }
                                />
                            )}
                            <Button
                                height="32px"
                                width="32px"
                                justifyContent="flex-start"
                                buttonColor="white"
                                onClick={onLoop}
                                icon={<FaRedoAlt style={{ fontSize: "16px" }} />}
                                isSelected={isLooping}
                            />
                        </SimpleControlsContainer>
                    </SongPlayerControls>
                </>
            )}
        </SongPlayerContainer>
    );
};

const SongsSection = ({
    songs,
    selectedSong,
    setSelectedSong,
    selectedInstruments,
    selectedStyle,
    isAutoPlayEnabled,
    createAndFetchMidi,
    setSelectedStyle,
    favorites,
    toggleFavorite,
    isFavorite,
    setSongSettingsIsOpen,
    updateSong,
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [activeSynths, setActiveSynths] = useState([]);
    const [isToneInitialized, setIsToneInitialized] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [isLooping, setIsLooping] = useState(false);
    const [downloadPopoverOpen, setDownloadPopoverOpen] = useState(false);
    const [downloadSong, setDownloadSong] = useState(null);
    const activeParts = useRef([]);
    const swiperRef = useRef(null);
    const lastPlayedSongRef = useRef(null);
    const previousSongsLengthRef = useRef(songs.length);
    const tuning = getTuning(selectedStyle);

    // Center the newly added song when songs.length increases
    useEffect(() => {
        if (!swiperRef.current) return;

        // Only act when a song was added
        if (songs.length > previousSongsLengthRef.current) {
            const lastIndex = songs.length - 1;

            // Let layout settle before sliding
            requestAnimationFrame(() => {
                if (swiperRef.current) {
                    swiperRef.current.slideTo(lastIndex, 0); // 0 = no animation
                }
            });
        }

        previousSongsLengthRef.current = songs.length;
    }, [songs.length]);

    // Center the selected song when user manually changes it
    useEffect(() => {
        if (!selectedSong || !swiperRef.current) return;

        const selectedIndex = songs.findIndex(song => song.name === selectedSong.name);
        if (selectedIndex !== -1 && selectedIndex !== swiperRef.current.activeIndex) {
            swiperRef.current.slideTo(selectedIndex, 300);
        }
    }, [selectedSong, songs]);

    const initializeTone = async () => {
        if (!isToneInitialized) {
            const newSynths = [];
            for (let i = 0; i < selectedInstruments.length; i++) {
                newSynths[i] = await loadSynth(selectedInstruments[i]);
            }
            // loadSynth already calls Tone.loaded(), but wait once more to ensure all buffers are ready
            await Tone.loaded();
            disposeSynths(activeSynths);
            setActiveSynths(newSynths);
            setIsToneInitialized(true);
            return newSynths;
        }
        return activeSynths;
    };

    const loadAndPlayMidiForSong = useCallback(async (song) => {
        if (!song || !song.midi) return;
        
        try {
            Tone.Transport.cancel(0);
            activeParts.current.forEach((part) => part.stop(0));
            activeParts.current.length = 0;

            await Tone.start().catch((error) => {
                console.log("Couldn't start AudioContext:", error);
            });

            if (activeSynths.length === 0) {
                console.log("No synths loaded.");
                return;
            }

            const { parts, maxDuration } = await createBufferedMidiEvents(
                song.midi.tracks,
                activeSynths,
                selectedInstruments,
                tuning
            );

            parts.forEach((part) => {
                activeParts.current.push(part);
                part.start(0);
            });

            setTotalDuration(maxDuration);
            // Start transport - this will begin playback
            // Make sure transport is stopped first, then start it
            if (Tone.Transport.state === "started") {
                Tone.Transport.stop();
            }
            Tone.Transport.start("+0.1");

            Tone.Transport.scheduleOnce(() => {
                if (isLooping) {
                    Tone.Transport.position = 0;
                    loadAndPlayMidiForSong(song);
                } else {
                    Tone.Transport.stop();
                    Tone.Transport.position = 0;
                    setProgress(0);
                    setIsPlaying(false);
                    // If autoplay is enabled, create a new song
                    if (isAutoPlayEnabled && selectedStyle) {
                        setTimeout(() => {
                            createAndFetchMidi(selectedStyle);
                        }, 100);
                    }
                }
            }, maxDuration);
        } catch (err) {
            console.error("An error occurred in loadAndPlayMidiForSong: ", err);
        }
    }, [selectedInstruments, activeSynths, tuning, isLooping, isAutoPlayEnabled, selectedStyle, createAndFetchMidi]);

    const loadAndPlayMidi = useCallback(async () => {
        if (!selectedSong || !selectedSong.midi) return;
        await loadAndPlayMidiForSong(selectedSong);
    }, [selectedSong, loadAndPlayMidiForSong]);

    useEffect(() => {
        // Match Player.js: call when isPlaying is true and synths are ready
        // Only call if song changed or we're starting to play (not already playing this song)
        if (isPlaying && selectedSong && activeSynths.length > 0) {
            const songKey = selectedSong.name;
            // Only load if this is a different song or we haven't started playing yet
            if (lastPlayedSongRef.current !== songKey || Tone.Transport.state !== "started") {
                lastPlayedSongRef.current = songKey;
                loadAndPlayMidiForSong(selectedSong);
            }
        }
    }, [activeSynths, isPlaying, selectedSong, loadAndPlayMidiForSong]);

    useEffect(() => {
        if (isPlaying && selectedSong) {
            const interval = setInterval(() => {
                const newProgress = (Tone.Transport.seconds / totalDuration) * 100;
                setProgress(newProgress);
            }, 100);
            return () => clearInterval(interval);
        }
    }, [isPlaying, totalDuration, selectedSong]);

    useEffect(() => {
        const updateSynths = async () => {
            if (!isToneInitialized) {
                return;
            }
            const newSynths = [];
            for (let i = 0; i < selectedInstruments.length; i++) {
                newSynths[i] = await loadSynth(selectedInstruments[i]);
            }
            disposeSynths(activeSynths);
            setActiveSynths(newSynths);
        };
    
        const debounceTimeout = setTimeout(() => {
            updateSynths();
        }, 300); 
    
        return () => clearTimeout(debounceTimeout);
    }, [selectedInstruments, isToneInitialized]);


    const handlePlayPause = async (song) => {
        // If clicking on a different song, switch to it and stop current playback
        const isDifferentSong = song !== selectedSong;
        if (isDifferentSong) {
            Tone.Transport.stop();
            Tone.Transport.position = 0;
            setProgress(0);
            setIsPlaying(false);
            setSelectedSong(song);
        }

        // Use the song parameter directly to avoid state timing issues
        const songToPlay = song || selectedSong;
        if (!songToPlay || !songToPlay.midi) return;

        // Initialize if needed (exactly like Player.js does)
        if (!isToneInitialized) {
            setIsInitializing(true);
            await Tone.loaded();
            await Tone.start();
            await initializeTone();
            // Wait 2 seconds like Player.js does to ensure all buffers are loaded
            await delay(2000);
            // Additional wait to ensure state has updated and all buffers are ready
            await delay(500);
            setIsInitializing(false);
        }

        // After initialization, continue with play/pause logic (exactly like Player.js)
        if (isPlaying && songToPlay === selectedSong) {
            // Pause if currently playing
            Tone.Transport.pause();
            setIsPlaying(false);
        } else {
            // Play - set playing state first, then loadAndPlayMidiForSong will be called by useEffect
            setIsPlaying(true);
        }
    };

    const handleProgressChange = (e) => {
        if (!selectedSong) return;
        const progressBar = e.target.closest(".progress-bar");
        if (!progressBar) return;
        const { left, width } = progressBar.getBoundingClientRect();
        const x = e.clientX - left;
        const newProgress = Math.min(Math.max((x / width) * 100, 0), 100);
        const newTime = (newProgress / 100) * totalDuration;
        setProgress(newProgress);
        Tone.Transport.seconds = newTime;
        if (isPlaying) {
            Tone.Transport.start("+0.1", newTime);
        }
    };

    const handleRewind = () => {
        if (!selectedSong) return;
        Tone.Transport.stop();
        Tone.Transport.position = 0;
        setProgress(0);
        if (isPlaying && selectedSong.midi) {
            loadAndPlayMidi();
        }
    };

    const handleLoop = () => {
        setIsLooping(!isLooping);
    };

    const handleDownload = (song) => {
        if (!song || !song.midi) return;
        setDownloadSong(song);
        setDownloadPopoverOpen(true);
    };

    const toggleDownloadPopover = () => {
        setDownloadPopoverOpen(!downloadPopoverOpen);
        if (downloadPopoverOpen) {
            setDownloadSong(null);
        }
    };

    return (
        <SongsSectionContainer>
            <SongsHeader>
                <SongsHeaderContent>
                    <SongsTitle>Songs</SongsTitle>
                </SongsHeaderContent>
            </SongsHeader>
            <SongsContent>
                <SongsBackground>
                    <Swiper
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        onAfterInit={(swiper) => {
                            // After initialization, center the selected song if it exists
                            if (selectedSong && songs.length > 0) {
                                const selectedIndex = songs.findIndex(song => song.name === selectedSong.name);
                                if (selectedIndex !== -1) {
                                    setTimeout(() => {
                                        swiper.slideTo(selectedIndex, 0);
                                    }, 100);
                                }
                            }
                        }}
                        direction="horizontal"
                        spaceBetween={6}
                        slidesPerView="auto"
                        centeredSlides={true}
                        centeredSlidesBounds={false}
                        grabCursor={true}
                        watchSlidesProgress={true}
                        initialSlide={(() => {
                            // Calculate initial slide based on selected song
                            if (selectedSong && songs.length > 0) {
                                const selectedIndex = songs.findIndex(song => song.name === selectedSong.name);
                                return selectedIndex !== -1 ? selectedIndex : Math.max(0, songs.length - 1);
                            }
                            return Math.max(0, songs.length - 1);
                        })()}
                        style={{
                            height: "100%",
                            width: "100%",
                        }}
                    >
                        {songs.map((song, index) => {
                            const isActive = song && selectedSong && song.name === selectedSong.name;
                            const songStyleColor = song?.style ? getStyleColor(song.style) : null;
                            const songBackgroundImage = song?.style ? getStyleBackgroundImage(song.style) : null;
                            return (
                                <StyledSwiperSlide key={index}>
                                    <SongContainers>
                                        <SongCard
                                            isActive={isActive}
                                            backgroundColor={songStyleColor}
                                            backgroundImage={songBackgroundImage}
                                            onClick={() => {
                                                if (song) {
                                                    setSelectedSong(song);
                                                    // Stop current playback if switching songs
                                                    if (isPlaying && !isActive) {
                                                        Tone.Transport.stop();
                                                        Tone.Transport.position = 0;
                                                        setProgress(0);
                                                        setIsPlaying(false);
                                                    }
                                                }
                                            }}
                                        >
                                            {song ? (
                                                <>
                                                    <SongHeader>
                                                        <HeaderButtonsContainer>
                                                            <EditButtonContainer
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (song && song.style) {
                                                                        setSelectedSong(song);
                                                                        setSongSettingsIsOpen(true);
                                                                    }
                                                                }}
                                                            >
                                                                <Button
                                                                    height="32px"
                                                                    width="32px"
                                                                    justifyContent="flex-start"
                                                                    buttonColor="white"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (song && song.style) {
                                                                            setSelectedSong(song);
                                                                            setSongSettingsIsOpen(true);
                                                                        }
                                                                    }}
                                                                    icon={<FaPen style={{ fontSize: "16px" }} />}
                                                                />
                                                            </EditButtonContainer>
                                                            <FavoriteButtonContainer
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleFavorite(song.name);
                                                                }}
                                                            >
                                                                <Button
                                                                    height="32px"
                                                                    width="32px"
                                                                    justifyContent="flex-start"
                                                                    buttonColor="white"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleFavorite(song.name);
                                                                    }}
                                                                    icon={
                                                                        isFavorite(song.name) ? (
                                                                            <FaHeart 
                                                                                style={{ 
                                                                                    fontSize: "16px"
                                                                                }} 
                                                                            />
                                                                        ) : (
                                                                            <FaRegHeart 
                                                                                style={{ 
                                                                                    fontSize: "16px"
                                                                                }} 
                                                                            />
                                                                        )
                                                                    }
                                                                />
                                                            </FavoriteButtonContainer>
                                                            <DownloadButtonContainer
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDownload(song);
                                                                }}
                                                            >
                                                                <Button
                                                                    height="32px"
                                                                    width="32px"
                                                                    justifyContent="flex-start"
                                                                    buttonColor="white"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDownload(song);
                                                                    }}
                                                                    icon={<FaDownload style={{ fontSize: "16px" }} />}
                                                                />
                                                            </DownloadButtonContainer>
                                                        </HeaderButtonsContainer>
                                                    </SongHeader>
                                                    <SongNameContainer>
                                                        <SongNameWrapper>
                                                            <SongNameText>
                                                                {song.name}
                                                            </SongNameText>
                                                        </SongNameWrapper>
                                                        {song.style && (
                                                            <StyleNameText>
                                                                {song.style.find((item) => item.name === "styleName")?.value || ""}
                                                            </StyleNameText>
                                                        )}
                                                    </SongNameContainer>
                                                    <SongPlayer
                                                        song={song}
                                                        isActive={isActive}
                                                        isPlaying={isActive && isPlaying}
                                                        progress={isActive ? progress : 0}
                                                        selectedInstruments={selectedInstruments}
                                                        selectedStyle={selectedStyle}
                                                        onPlayPause={(e) => {
                                                            e.stopPropagation();
                                                            handlePlayPause(song);
                                                        }}
                                                        onProgressChange={handleProgressChange}
                                                        onRewind={(e) => {
                                                            e.stopPropagation();
                                                            handleRewind();
                                                        }}
                                                        onLoop={(e) => {
                                                            e.stopPropagation();
                                                            handleLoop();
                                                        }}
                                                        isLooping={isActive ? isLooping : false}
                                                        isInitializing={isActive ? isInitializing : false}
                                                    />
                                                </>
                                            ) : null}
                                        </SongCard>
                                    </SongContainers>
                                </StyledSwiperSlide>
                            );
                        })}
                    </Swiper>
                </SongsBackground>
            </SongsContent>
            {downloadPopoverOpen && downloadSong && (
                <Popover
                    content={
                        <PopoverContentContainer
                            title="DOWNLOAD"
                            component={
                                <Download
                                    handleWAVDownloadClick={() =>
                                        handleWAVDownloadClick(
                                            downloadSong.midi,
                                            selectedInstruments
                                        )
                                    }
                                    selectedSong={downloadSong}
                                />
                            }
                        />
                    }
                    onClose={toggleDownloadPopover}
                    zIndex={1000}
                />
            )}
        </SongsSectionContainer>
    );
};

export default SongsSection;

